/*
 * @Author: cheng
 * @Date: 2018-12-21 11:30:04
 * @Last Modified by: cheng
 * @Last Modified time: 2019-03-28 11:46:1:538
 *
 */
import * as React from 'react';
import { API, REFERENCE_SUFFIX, ENTITY_RECOGNIZER, ENTITY_NAME_KOTLIN_STRING } from '@config/index';
import { cloneDeep } from 'lodash';
import { CloseOutlined, RightOutlined, LeftOutlined } from '@ant-design/icons';
import { Input, InputNumber, Popover } from 'antd';
import { ReferenceConfig, ReferenceUpdateConfig } from './reference-textarea';
import { localizedString } from '@locale/index';
import {
  PositionConfig,
  ParameterType,
  ParameterConfig,
  SlotType,
  DataQuery
} from '@interface/index';
import { ParameterMapStore } from '@lib/index';

const Search = Input.Search;

enum REFERENCE_MENU_ITEM_TYPE {
  ROOT_ANY = 'root-any',
  ROOT_ALL = 'root-all',
  RAW = 'raw',
  FUNCTION_PARAM = 'function_param',
  PARAMETER_ENTITY = 'parameter-entity',
  VALUE = 'entity-value',
  VALUE_ORIGINAL = 'entity-value-original',
  ENTITY_FUNCTION = 'entity-function',
  PARAMETER_COMPOSITE = 'parameter-composite',
  COMPOSITE = 'composite',
  CLOSURE = 'closure',
  SIZE = 'size',
  MULTI_INDEX = 'multi_index',
  BODY_IT = 'it',
  CLOSURE_IT_INDEX = 'it_index',
  PARAMETER_COMPANION = 'parameter-companion',
  SELF = 'self',
  DEFAULT = ''
}

export type ReferenceMenuItem = {
  id: string;
  name: string;
  type: REFERENCE_MENU_ITEM_TYPE;
  attributes?: any;
  type_id?: string;
  type_name?: string;
  multi_value?: boolean;
  index?: number;
  prevItem?: ReferenceMenuItem;
  disabled?: boolean;
  popoverText?: string;
};

export interface MenuConfig {
  position: PositionConfig;
  rangeX: number;
  rangeX2: number;
}

export interface ReferenceMenuPropsConfig {
  tag: string;
  visible: boolean;
  referenceData: ReferenceConfig;
  initParameters: ParameterConfig[];
  textAreaRef?: HTMLTextAreaElement;
  menuConf: MenuConfig;
  bodyItTypeId?: string;
  forPSQL?: boolean;
  update: (nextReferenceData: ReferenceUpdateConfig) => void;
  close: () => void;
  onRawCodeSelected: (menuConf: MenuConfig) => void;
  onFunctionParam: (paramName: string, menuConf: MenuConfig) => void;
  prefix?: string[];
  forExpression?: boolean;
}
export interface ReferenceMenuStateConfig {
  curParameters: ReferenceMenuItem[];
  filterText: string;
  prefix: string[];
  appendText: string;
  appendIds: string[];
  prevName: string;
  // status
  systemError: boolean;
  parameters: ParameterConfig[];
  parameterStack: ReferenceMenuItem[][];
  textStack: string[];
  idStack: string[][];
}

let defaultState: ReferenceMenuStateConfig = {
  curParameters: [],
  filterText: '',
  prefix: ['$', '.'],
  appendText: '',
  appendIds: [],
  prevName: localizedString('reference_source_list'),
  systemError: false,
  parameters: [],
  parameterStack: [],
  textStack: [],
  idStack: []
};

export class ReferenceMenu extends React.Component<
  ReferenceMenuPropsConfig,
  ReferenceMenuStateConfig
> {
  private $container?: HTMLDivElement;
  constructor(props: ReferenceMenuPropsConfig) {
    super(props);

    this.state = defaultState;
  }

  componentDidMount() {
    this.onInit();
  }

  onInit = async () => {
    const { initParameters, textAreaRef } = this.props;
    const curParameters: ReferenceMenuItem[] = await this.getNextParameters({
      type: REFERENCE_MENU_ITEM_TYPE.DEFAULT,
      id: '',
      name: '',
      attributes: initParameters
    });

    this.setState({
      curParameters,
      filterText: ''
    });

    if (textAreaRef) {
      textAreaRef.blur();
    }
    document.body.addEventListener('click', this.handleVisible, false);
  }

  componentWillUnmount() {
    const { textAreaRef, menuConf } = this.props;
    const { appendText } = this.state;

    document.body.removeEventListener('click', this.handleVisible, false);

    setTimeout(() => {

      const index = menuConf.rangeX + appendText.length;

      if (textAreaRef) {
        textAreaRef.focus();
        textAreaRef.setSelectionRange(index, index);
      }

      defaultState = {
        curParameters: [],
        filterText: '',
        prefix: ['$', '.'],
        appendText: '',
        appendIds: [],
        prevName: localizedString('reference_source_list'),
        systemError: false,
        parameters: [],
        parameterStack: [],
        textStack: [],
        idStack: []
      };
    });
  }

  render() {
    const { tag, menuConf } = this.props;
    const {
      filterText,
      curParameters,
      prevName,
      parameterStack,
      textStack,
      idStack,
      appendIds
    } = this.state;

    const filterData = filterText
      ? curParameters.filter((item: ReferenceMenuItem) =>
        item.name.includes(filterText)
      )
      : curParameters;

    return (
      <div
        className="reference-menu__container"
        ref={this.containerRef}
        style={{
          position: 'absolute',
          left: menuConf.position.x,
          top: menuConf.position.y
        }}
        tabIndex={0}
        data-tag={tag}
      >
        <div className="reference-menu__item reference-menu__header">
          {parameterStack.length &&
            <LeftOutlined
              onClick={() => {

                const curParameters = parameterStack.pop() || [];
                const appendText = textStack.pop() || '';
                const appendIds = idStack.pop() || [];

                this.setState({
                  curParameters,
                  parameterStack,
                  textStack,
                  idStack,
                  appendText,
                  appendIds
                });
              }}
            /> || undefined
          }
          <span
            className="menu-title"
            title={prevName}>
            {prevName}
          </span>
          <CloseOutlined
            onClick={() => {
              this.props.close();
            }} />
        </div>

        {/* <div className="reference-menu__item reference-item__filter">
          <Search
            data-tag={tag}
            placeholder={localizedString('common_search_placeholder')}
            value={filterText}
            style={{ width: 184, height: 32 }}
            onChange={this.handleChangeFilter}
          />
        </div> */}

        <div className="reference-menu__main">
          <ul className="reference-menu" key={appendIds.join('.')}>
            {filterData.map((reference: ReferenceMenuItem) => {

              let optionComp: any = reference.name;

              if (reference.popoverText) {
                optionComp = <Popover content={reference.popoverText}>
                  <span style={reference.disabled ? { color: 'rgba(0,0,0,.26)', cursor: 'not-allowed' } : {}}>
                    {
                      reference.name
                    }
                  </span>
                </Popover>;
              }

              return <li
                className="reference-menu__item"
                key={reference.id + reference.name}
                onClick={!reference.disabled && (() => {
                  this.handleSelect(reference);
                }) || undefined}
                title={reference.name}
              >
                {reference.type === REFERENCE_MENU_ITEM_TYPE.MULTI_INDEX
                  ? <div onClick={(e: any) => {
                    e.stopPropagation();
                  }}>
                    <span>index: </span>
                    <InputNumber
                      // style={{ height: 30 }}
                      disabled={reference.disabled}
                      key={reference.id}
                      defaultValue={0}
                      min={0}
                      onChange={(value: any) => {
                        reference.index = value;
                      }}
                    />
                    <RightOutlined
                      style={{ padding: '0 12px', width: 44 }}
                      onClick={() => {
                        this.handleSelect(reference);
                      }} />
                  </div>
                  : optionComp}
              </li>;
            }
            )}
          </ul>
        </div>
      </div>
    );
  }


  handleRawCode = () => {
    this.props.onRawCodeSelected(this.props.menuConf);
  }

  containerRef = (ref: HTMLDivElement) => {
    this.$container = ref;
  };

  handleVisible = (e: any) => {
    if (!this.$container || !this.$container.contains(e.target)) {
      this.props.referenceData.text = this.props.referenceData.text.substring(
        0,
        this.props.referenceData.text.length - 1
      );
      this.props.update({ nextReferenceData: this.props.referenceData });
      this.props.close();
    }
  };

  handleChangeFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      filterText: e.target.value
    });
  };


  isEntityType = (typeId: string) => {
    const param = ParameterMapStore.get(typeId);
    return param.type === ParameterType.ENTITY;
  }

  isBaseType = (typeId: string) => {

    const param = ParameterMapStore.get(typeId);

    if (!param || param.type !== ParameterType.ENTITY) {
      return false;
    }

    const isList = param.recognizer &&
      param.recognizer.indexOf(ENTITY_RECOGNIZER.LIST) !== -1;

    return !isList;
  }

  handleSelect = async (selected: ReferenceMenuItem) => {
    const { referenceData, update, close, forExpression } = this.props;

    const { appendIds, parameterStack, textStack, idStack } = this.state;
    let { appendText } = this.state;
    const nextReferenceData: ReferenceConfig = cloneDeep(referenceData);
    const selectedClone = cloneDeep(selected);
    let curParameters: any[] = [];

    parameterStack.push(cloneDeep(this.state.curParameters));
    textStack.push(cloneDeep(appendText));
    idStack.push(cloneDeep(appendIds));

    let finish = false;
    switch (selectedClone.type) {
      case REFERENCE_MENU_ITEM_TYPE.SELF:
        finish = true;
        break;
      case REFERENCE_MENU_ITEM_TYPE.RAW:
        close();
        this.handleRawCode();
        return;
      case REFERENCE_MENU_ITEM_TYPE.FUNCTION_PARAM:

        appendText += `${selectedClone.name}_param`;
        appendIds.push(`${selectedClone.name}_param`);

        if (forExpression ||
          // (forPSQL && this.isEntityType(selectedClone.type_id || '')) ||
          (!selectedClone.multi_value &&
            this.isBaseType(selectedClone.type_id || ''))
        ) {
          finish = true;
        }
        break;
      case REFERENCE_MENU_ITEM_TYPE.PARAMETER_ENTITY:
      case REFERENCE_MENU_ITEM_TYPE.PARAMETER_COMPANION: {
        appendText += !appendText
          ? selectedClone.name
          : `.${selectedClone.name}`;
        appendIds.push(selectedClone.id);

        if (forExpression ||
          // (forPSQL && this.isEntityType(selectedClone.type_id || '')) ||
          (!selectedClone.multi_value &&
            this.isBaseType(selectedClone.type_id || ''))
        ) {
          finish = true;
        }
      }
        break;
      case REFERENCE_MENU_ITEM_TYPE.PARAMETER_COMPOSITE:
        finish = false;
        appendText += !appendText
          ? selectedClone.name
          : `.${selectedClone.name}`;
        appendIds.push(selectedClone.id);
        if (forExpression) {
          finish = true;
        }
        break;
      case REFERENCE_MENU_ITEM_TYPE.CLOSURE: {
        const param = ParameterMapStore.get(selectedClone.id);

        if (param.proto &&
          param.proto.type === ParameterType.ENTITY &&
          !param.multiValue &&
          this.isBaseType(selectedClone.type_id || '')
        ) {
          finish = true;
        }
        appendText += !appendText
          ? `${selectedClone.name}`
          : `.${selectedClone.name}`;
        appendIds.push(`${selectedClone.id
          }_${REFERENCE_SUFFIX.CLOSURE
          }`);
        break;
      }
      case REFERENCE_MENU_ITEM_TYPE.BODY_IT: {
        appendText = 'it';
        appendIds.push('it');

        if (this.isBaseType(selectedClone.type_id || '')) {
          finish = true;
        }

        break;
      }
      case REFERENCE_MENU_ITEM_TYPE.CLOSURE_IT_INDEX: {
        appendText = 'index';
        appendIds.push('index');
        finish = true;
        break;
      }
      case REFERENCE_MENU_ITEM_TYPE.SIZE: {
        finish = true;
        appendText += '.size';
        appendIds.push('size');
        break;
      }
      case REFERENCE_MENU_ITEM_TYPE.MULTI_INDEX: {
        finish = false;
        const prev = selectedClone.prevItem;
        if (prev && this.isBaseType(prev.type_id || '')) {
          finish = true;
        }
        appendText += `[${selectedClone.index}]`;
        appendIds[appendIds.length - 1] = appendIds[appendIds.length - 1] + `[${selected.index}]`;
      }
        break;
      case REFERENCE_MENU_ITEM_TYPE.VALUE:
        finish = true;
        break;
      case REFERENCE_MENU_ITEM_TYPE.VALUE_ORIGINAL:
        finish = true;
        appendText += '.original';
        appendIds.push('original');
        break;
      default:
        break;
    }

    if (finish) {
      const curValue = this.insertAtCursor(`${appendText}$`);
      nextReferenceData.text = curValue || '';

      nextReferenceData.labels.set(`$${appendText}$`, appendIds);

      update({
        nextReferenceData
      });

      this.setState(
        {
          appendText: `${appendText}$`
        },
        () => {
          close();
        }
      );

      return;
    }

    curParameters = await this.getNextParameters(selectedClone);
    this.setState({
      curParameters,
      appendText,
      appendIds,
      parameterStack,
      textStack,
      idStack,
      prevName: selected.name,
      filterText: ''
    });
  };

  insertAtCursor = (value: string) => {
    const state = this.state;
    const props = this.props;
    const curValue = props.referenceData.text;

    if (!props.textAreaRef) {
      return;
    }

    props.textAreaRef.value =
      curValue.substring(0, props.menuConf.rangeX) +
      value +
      curValue.substring(props.menuConf.rangeX2, curValue.length);

    props.textAreaRef.selectionStart = props.menuConf.rangeX + value.length;
    props.textAreaRef.selectionEnd = props.menuConf.rangeX2 + value.length;

    props.textAreaRef.dispatchEvent(new Event('input'));

    return props.textAreaRef.value;
  };

  getNextParameters = async (selected: ReferenceMenuItem) => {
    const state = this.state;
    const props = this.props;
    const result: ReferenceMenuItem[] = [];

    const addNormalOptions = () => {
      const proto = ParameterMapStore.get(selected.type_id || '');
      const attrIds: string[] = [];
      ((proto && proto.attributes) || selected.attributes || []).map((attr: any) =>
        typeof attr === 'string' ? attr : (attr.id || attr.attribute_id)
      ).forEach((attrId: string) => {
        attrIds.push(attrId);
      });

      if (props.forPSQL && this.isEntityType(selected.type_id || '')) {
        result.push({
          id: 'self',
          type: REFERENCE_MENU_ITEM_TYPE.SELF,
          name: '(SELF)'
        });
      }

      for (const item of (proto && proto.attributes || [])) {

        if (item.type === ParameterType.CLOSURE && item.isStatic) {
          continue;
        }

        const disabled = item.type &&
          item.proto.type === ParameterType.ENTITY &&
          props.forExpression &&
          (!item.triggers ||
            !item.triggers.length);
        const popoverText = disabled && localizedString('reference_item_slot_trigger_hint') || undefined;

        result.push({
          disabled,
          popoverText,
          id: item.id,
          type: item.type === ParameterType.CLOSURE
            ? REFERENCE_MENU_ITEM_TYPE.CLOSURE
            : item.proto.type === SlotType.entity
              ? REFERENCE_MENU_ITEM_TYPE.PARAMETER_ENTITY
              : REFERENCE_MENU_ITEM_TYPE.PARAMETER_COMPOSITE,
          name: item.name,
          multi_value: item.multi_value,
          attributes: item.attributes,
          type_id: item.proto.id,
          type_name: item.proto.name
        });
        if (item.proto.type === SlotType.entity) {

          if (
            item.attribute_companions &&
            item.attribute_companions.length
          ) {
            for (const cp of item.attribute_companions) {
              result.push({
                id: cp.id,
                name: `${item.name}_${cp.name}`,
                type: REFERENCE_MENU_ITEM_TYPE.PARAMETER_COMPANION,
                attributes: []
              });
            }
          }
        }
      }
    };

    const addMulitValueOptions = () => {

      if (props.forPSQL && this.isEntityType(selected.type_id || '')) {
        result.push({
          id: 'self',
          type: REFERENCE_MENU_ITEM_TYPE.SELF,
          name: '(SELF)'
        });
      }

      result.push({
        id: 'size',
        type: REFERENCE_MENU_ITEM_TYPE.SIZE,
        name: 'size'
      });

      result.push({
        id: 'index',
        type: REFERENCE_MENU_ITEM_TYPE.MULTI_INDEX,
        name: 'index',
        index: 0,
        attributes: selected.attributes && selected.attributes.map(
          (attr: any) => attr.attribute_id || attr.id || attr
        ),
        prevItem: selected,
        type_id: selected.type_id
      });
    };

    const addValueOptions = () => {
      let param: ParameterConfig | undefined;
      if (selected.type === REFERENCE_MENU_ITEM_TYPE.MULTI_INDEX) {
        const id = selected.prevItem && selected.prevItem.id || '';
        param = ParameterMapStore.get(id);
      } else {
        param = ParameterMapStore.get(selected.id);
      }

      const typeId = selected.type_id || (selected.prevItem && selected.prevItem.type_id);
      const type = typeId && ParameterMapStore.get(typeId);

      if (type && type.attributes) {
        for (const dq of type.attributes.filter((dq: DataQuery) =>
          dq.frame && dq.frame.name === ENTITY_NAME_KOTLIN_STRING)) {
          result.push({
            id: dq.id,
            type: REFERENCE_MENU_ITEM_TYPE.CLOSURE,
            name: dq.name,
            type_id: dq.frame && dq.frame.id,
            multi_value: dq.multiValue
          });
        }
      }
    };

    switch (selected.type) {
      case REFERENCE_MENU_ITEM_TYPE.PARAMETER_ENTITY:
      case REFERENCE_MENU_ITEM_TYPE.FUNCTION_PARAM: {
        if (selected.multi_value) {
          addMulitValueOptions();
        } else {
          addNormalOptions();
        }
      }
        break;
      case REFERENCE_MENU_ITEM_TYPE.PARAMETER_COMPANION: {
        const param = ParameterMapStore.get(selected.id);

        if (!param.multiValue) {
          addValueOptions();
        } else {
          addMulitValueOptions();
        }
      }
        break;
      case REFERENCE_MENU_ITEM_TYPE.PARAMETER_COMPOSITE: {
        if (selected.multi_value) {
          addMulitValueOptions();
        } else {
          await addNormalOptions();
        }
      }
        break;
      case REFERENCE_MENU_ITEM_TYPE.CLOSURE: {
        const param = ParameterMapStore.get(selected.id);
        if (param.multiValue) {
          addMulitValueOptions();
        } else {
          await addNormalOptions();
        }
      }
        break;
      case REFERENCE_MENU_ITEM_TYPE.BODY_IT: {
        addNormalOptions();
      }
        break;
      case REFERENCE_MENU_ITEM_TYPE.MULTI_INDEX: {
        const prev = selected.prevItem;
        if (prev
          && prev.type === REFERENCE_MENU_ITEM_TYPE.PARAMETER_COMPANION) {
          addValueOptions();
        } else {
          addNormalOptions();
        }
      }
        break;

      default: {
        if (props.bodyItTypeId) {
          const tp = ParameterMapStore.get(props.bodyItTypeId);

          result.push({
            id: 'it',
            type: REFERENCE_MENU_ITEM_TYPE.BODY_IT,
            name: 'it',
            attributes: tp.attributes &&
              tp.attributes.map((attr: any) =>
                attr.attribute_id || attr.id || attr
              ),
            type_id: props.bodyItTypeId
          });

          result.push({
            id: 'index',
            type: REFERENCE_MENU_ITEM_TYPE.CLOSURE_IT_INDEX,
            name: 'index'
          });
        }

        if (!props.forExpression) { // 当非expression时
          result.push({
            id: 'raw_code',
            type: REFERENCE_MENU_ITEM_TYPE.RAW,
            name: '\${RAW CODE}'
          });
        }

        for (const item of selected.attributes || []) {

          if (item.type === ParameterType.FUNCTION_PARAM) {
            result.push({
              id: item.name,
              type: REFERENCE_MENU_ITEM_TYPE.FUNCTION_PARAM,
              name: item.name,
              multi_value: item.multiValue,
              type_id: item.proto && item.proto.id,
              type_name: item.proto && item.proto.name
            });
            continue;
          }

          if (item.proto && item.proto.name === ENTITY_NAME_KOTLIN_STRING) {
            continue;
          }

          let attributes: string[] | undefined;

          if (item.type === ParameterType.CLOSURE) {
            const closure = ParameterMapStore.get(item.id);
            attributes = closure.attributes;
          } else {
            attributes = item.attributes &&
              item.attributes.map(
                (attr: any) => attr.attribute_id || attr.id || attr
              );
          }

          const disabled = item.proto &&
            item.proto.type === ParameterType.ENTITY &&
            props.forExpression &&
            (!item.triggers ||
              !item.triggers.length);

          const popoverText = disabled && localizedString('reference_item_slot_trigger_hint') || undefined;

          result.push({
            disabled,
            popoverText,
            attributes,
            id: item.id,
            type: item.type === ParameterType.CLOSURE
              ? REFERENCE_MENU_ITEM_TYPE.CLOSURE
              : (
                item.proto && item.proto.type === ParameterType.ENTITY
                  ? REFERENCE_MENU_ITEM_TYPE.PARAMETER_ENTITY
                  : REFERENCE_MENU_ITEM_TYPE.PARAMETER_COMPOSITE
              ),
            multi_value: item.multiValue,
            name: item.name,
            type_id: item.proto && item.proto.id,
            type_name: item.proto && item.proto.name
          });

          if (item.name !== 'typeExpression' && item.proto && item.proto.type === ParameterType.ENTITY) {
            if (this.getCurentFrame().attributes.find((attr: string) => attr === item.id)) {

              const peRes: any = await API.common
                .getAttributeCompletedInfoById([item.id], this.getCurentFrame().id)
                .catch((err: Error) => {
                  throw err;
                });

              if (
                peRes &&
                peRes[0] &&
                peRes[0].attribute_companions &&
                peRes[0].attribute_companions.length
              ) {
                for (const cp of peRes[0].attribute_companions) {
                  result.push({
                    id: cp.id,
                    name: `${item.name}_${cp.name}`,
                    type: REFERENCE_MENU_ITEM_TYPE.PARAMETER_COMPANION,
                    multi_value: cp.multiValue,
                    attributes: []
                  });
                }
              }
            }
          }
        }
      }
        break;
    }

    return result;
  }

  getCurentFrame() {
    return JSON.parse(window.sessionStorage.getItem('curFrame') || '{}');
  }
}
